import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import translationEN from "./locales/en/translation.json";
import translationJP from "./locales/jp/translation.json";

import bookingConfirmationEN from "./pages/front-pages/booking-confirmation/translation/en.json";
import bookingConfirmationJP from "./pages/front-pages/booking-confirmation/translation/jp.json";
import fleetPageEN from "./pages/front-pages/fleet/translation/en.json";
import fleetPageJP from "./pages/front-pages/fleet/translation/jp.json";
import businessRegisterTranslationEN from "./pages/front-pages/business-register/translation/en.json";
import businessRegisterTranslationJP from "./pages/front-pages/business-register/translation/jp.json";
import individualRegisterTranslationEN from "./pages/front-pages/register/translation/en.json";
import individualRegisterTranslationJP from "./pages/front-pages/register/translation/jp.json";
import emailVerificationTranslationEN from "./pages/front-pages/emailverification/translation/en.json";
import emailVerificationTranslationJP from "./pages/front-pages/emailverification/translation/jp.json";
import authComponentEN from "./components/auth/translation/en.json";
import authComponentJP from "./components/auth/translation/jp.json";
import changeEmailEN from "./pages/front-pages/change-email/translation/en.json";
import changeEmailJP from "./pages/front-pages/change-email/translation/jp.json";
import changeEmailVerificationEN from "./pages/front-pages/change-email-verification/translation/en.json";
import changeEmailVerificationJP from "./pages/front-pages/change-email-verification/translation/jp.json";
import changePasswordEN from "./pages/front-pages/change-password/translation/en.json";
import changePasswordJP from "./pages/front-pages/change-password/translation/jp.json";
import myPageEN from "./pages/front-pages/my-page/translation/en.json";
import myPageJP from "./pages/front-pages/my-page/translation/jp.json";
import revertChangesEN from "./pages/front-pages/revert-changes/translation/en.json";
import revertChangesJP from "./pages/front-pages/revert-changes/translation/jp.json";
import bookingDetailEN from "./pages/front-pages/booking-detail/translation/en.json";
import bookingDetailJP from "./pages/front-pages/booking-detail/translation/jp.json";
import checkoutEN from "./pages/front-pages/checkout/translation/en.json";
import checkoutJP from "./pages/front-pages/checkout/translation/jp.json";
import memorandumEN from "./pages/front-pages/memorandum/translation/en.json";
import memorandumJP from "./pages/front-pages/memorandum/translation/jp.json";
import cartEN from "./pages/front-pages/cart/translation/en.json";
import cartJP from "./pages/front-pages/cart/translation/jp.json";
import componentTncEN from "./components/cancellation-policy/translation/en.json";
import componentTncJP from "./components/cancellation-policy/translation/jp.json";
import paymentSuccessEN from "./pages/front-pages/payment-success/translation/en.json";
import paymentSuccessJP from "./pages/front-pages/payment-success/translation/jp.json";
import disclaimerEN from "./components/disclaimer/translation/en.json";
import disclaimerJP from "./components/disclaimer/translation/jp.json";
import contactEN from "./pages/front-pages/contact/translation/en.json";
import contactJP from "./pages/front-pages/contact/translation/jp.json";
import airportTransferEN from "./pages/front-pages/airport-transfer/translation/en.json";
import airportTransferJP from "./pages/front-pages/airport-transfer/translation/jp.json";
import componentTotalCardEN from "./components/total-card/translation/en.json";
import componentTotalCardJP from "./components/total-card/translation/jp.json";
import editBookingEN from "./pages/front-pages/booking-edit/translation/en.json";
import editBookingJP from "./pages/front-pages/booking-edit/translation/jp.json";
import servicesPageEN from "./pages/front-pages/services/translation/en.json";
import servicesPageJP from "./pages/front-pages/services/translation/jp.json";
import meetAssistInquiryEN from "./pages/front-pages/meet-assist-inquiry/translation/en.json";
import meetAssistInquiryJP from "./pages/front-pages/meet-assist-inquiry/translation/jp.json";

/* --------------------------------------------------
   Resolve language BEFORE i18n init
-------------------------------------------------- */
const SUPPORTED_LANGS = ["en", "jp"];
const DEFAULT_LANG = "jp";

const resolveLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANG;

  // 1. Force check URL first
  const pathParts = window.location.pathname.split("/");
  const urlLang = pathParts[1]; // Gets 'en' or 'jp' from /en/dashboard

  if (SUPPORTED_LANGS.includes(urlLang)) {
    return urlLang;
  }

  // 2. Fallback to localStorage
  const savedLang = localStorage.getItem("lang")?.toLowerCase();
  if (SUPPORTED_LANGS.includes(savedLang)) {
    return savedLang;
  }

  return DEFAULT_LANG;
};

/* --------------------------------------------------
   Apply language to <html>
-------------------------------------------------- */
const applyLanguageToHtml = (lng) => {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  html.setAttribute("lang", lng);

  if (lng === "jp" || lng === "JP") {
    html.classList.add("lang-jp");
  } else {
    html.classList.remove("lang-jp");
  }
};

/* --------------------------------------------------
   Resources
-------------------------------------------------- */
const resources = {
  en: {
    translation: {
      ...translationEN,
      BookingConfirmation: bookingConfirmationEN,
      FleetPage: fleetPageEN,
      component: { auth: authComponentEN },
      individualRegister: individualRegisterTranslationEN,
      businessRegister: businessRegisterTranslationEN,
      emailVerification: emailVerificationTranslationEN,
      myPage: myPageEN,
      changeEmail: changeEmailEN,
      changeEmailVerification: changeEmailVerificationEN,
      revertChanges: revertChangesEN,
      changePassword: changePasswordEN,
      bookingDetail: bookingDetailEN,
      checkout: checkoutEN,
      memorandum: memorandumEN,
      cart: cartEN,
      componentTnc: componentTncEN,
      paymentSuccess: paymentSuccessEN,
      componentDisclaimer: disclaimerEN,
      contact: contactEN,
      AirportTransfer: airportTransferEN,
      componentTotalCard: componentTotalCardEN,
      editBooking: editBookingEN,
      servicesPage: servicesPageEN,
      meetAssistInquiry: meetAssistInquiryEN,
    },
  },
  jp: {
    translation: {
      ...translationJP,
      BookingConfirmation: bookingConfirmationJP,
      FleetPage: fleetPageJP,
      component: { auth: authComponentJP },
      individualRegister: individualRegisterTranslationJP,
      businessRegister: businessRegisterTranslationJP,
      emailVerification: emailVerificationTranslationJP,
      myPage: myPageJP,
      changeEmail: changeEmailJP,
      changeEmailVerification: changeEmailVerificationJP,
      revertChanges: revertChangesJP,
      changePassword: changePasswordJP,
      bookingDetail: bookingDetailJP,
      checkout: checkoutJP,
      memorandum: memorandumJP,
      cart: cartJP,
      componentTnc: componentTncJP,
      paymentSuccess: paymentSuccessJP,
      componentDisclaimer: disclaimerJP,
      contact: contactJP,
      AirportTransfer: airportTransferJP,
      componentTotalCard: componentTotalCardJP,
      editBooking: editBookingJP,
      servicesPage: servicesPageJP,
      meetAssistInquiry: meetAssistInquiryJP,
    },
  },
};

/* --------------------------------------------------
   i18n init (CRITICAL FIX)
-------------------------------------------------- */
const initialLanguage = resolveLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage, // This must be the source of truth
    fallbackLng: false,   // Disable fallback during debugging to prevent 'en' override
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

// Apply classes/attributes
applyLanguageToHtml(initialLanguage);

// Only update localStorage, don't force a redirect here
i18n.on("languageChanged", (lng) => {
  localStorage.setItem("lang", lng.toLowerCase());
  applyLanguageToHtml(lng);
});

export default i18n;
